<script setup lang="ts">
import { Check, ChevronRight, MoreHorizontal, Plus, X } from "@lucide/vue";
import { computed, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";

import type { ServiceCategory } from "@/entities/service-category";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from "@/shared/ui";
import { toast } from "@/shared/lib";

import { useServicesCatalogStore } from "../model/store";

/**
 * Рядок-заголовок категорії — feature-local (ADR-0007 не тригериться, не в shared/ui).
 * Стейт-машина: idle | editing | pendingRename | deleteConfirm | pendingDelete.
 * Дії через стор — той самий feature-слайс.
 * category === null → статичний muted-заголовок «Без категорії», без дій.
 */
const props = defineProps<{
  category: ServiceCategory | null;
  serviceCount?: number;
}>();

type CatMode = "idle" | "editing" | "pendingRename" | "deleteConfirm" | "pendingDelete";

const { t } = useI18n();
const store = useServicesCatalogStore();

const mode = ref<CatMode>("idle");
const editText = ref("");
const editInputRef = ref<{ $el: HTMLInputElement } | null>(null);
const cancelDeleteRef = ref<{ $el: HTMLElement } | null>(null);

const count = computed(() => props.serviceCount ?? 0);

const badgeClass = computed(() => {
  const base = "rounded-full border px-2 py-0.5 text-xs";
  if (props.category !== null && count.value > 0) {
    return `${base} border-primary/30 text-primary`;
  }
  return `${base} border-border text-muted-foreground`;
});

async function startRename(): Promise<void> {
  if (!props.category) return;
  editText.value = props.category.name;
  mode.value = "editing";
  await nextTick();
  editInputRef.value?.$el.focus();
  editInputRef.value?.$el.select();
}

async function cancelRename(): Promise<void> {
  mode.value = "idle";
}

async function commitRename(): Promise<void> {
  if (mode.value !== "editing" || !props.category) return;
  const name = editText.value.trim();
  if (!name || name === props.category.name) {
    await cancelRename();
    return;
  }
  mode.value = "pendingRename";
  try {
    await store.renameCategory(props.category.id, name);
    mode.value = "idle";
  } catch {
    mode.value = "editing";
    toast.error(t("errors.server"));
    await nextTick();
    editInputRef.value?.$el.focus();
  }
}

function startDelete(): void {
  mode.value = "deleteConfirm";
  nextTick(() => {
    cancelDeleteRef.value?.$el.focus();
  });
}

async function cancelDelete(): Promise<void> {
  mode.value = "idle";
}

async function confirmDelete(): Promise<void> {
  if (!props.category) return;
  mode.value = "pendingDelete";
  try {
    await store.removeCategory(props.category.id);
    // Компонент зникне з DOM (групу прибрано з categories), mode не треба скидати.
  } catch {
    mode.value = "deleteConfirm";
    toast.error(t("errors.server"));
  }
}
</script>

<template>
  <div
    class="category-row group flex flex-col gap-1.5 border-l-[3px] bg-accent px-3 py-2 transition-colors"
    :class="
      category === null
        ? 'border-dashed border-l-muted-foreground'
        : 'border-l-primary'
    "
    data-testid="category-row"
  >
    <!-- null category — static muted header, no actions -->
    <div
      v-if="category === null"
      class="flex items-center gap-2"
    >
      <span class="min-w-0 flex-1 truncate text-sm font-semibold text-muted-foreground">
        {{ t("services.categories.uncategorized") }}
      </span>
      <span
        data-testid="count-badge"
        class="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
      >
        {{ t("services.categories.count", { count }) }}
      </span>
    </div>

    <!-- named category — idle -->
    <div
      v-else-if="mode === 'idle'"
      class="flex items-center gap-2"
    >
      <!-- Collapse caret + name — one disclosure button (MUST-2) -->
      <button
        data-testid="collapse-caret"
        type="button"
        class="flex flex-1 min-w-0 items-center gap-2 text-left max-sm:min-h-11"
        :aria-label="store.isCategoryCollapsed?.(category.id) ? t('services.a11y.expandCategory', { name: category.name }) : t('services.a11y.collapseCategory', { name: category.name })"
        :aria-expanded="!store.isCategoryCollapsed?.(category.id)"
        @click="store.toggleCategoryCollapse?.(category.id)"
      >
        <span
          class="shrink-0 transition-transform"
          :class="{ 'rotate-90': !store.isCategoryCollapsed?.(category.id) }"
        >
          <ChevronRight
            :size="16"
            aria-hidden="true"
          />
        </span>
        <span class="min-w-0 flex-1 line-clamp-2 [overflow-wrap:anywhere] text-sm font-bold text-foreground">
          {{ category.name }}
        </span>
      </button>

      <!-- Count badge -->
      <span
        data-testid="count-badge"
        :class="badgeClass"
      >
        {{ t("services.categories.count", { count }) }}
      </span>

      <!-- Desktop ghost "Послуга" button: mobile hidden, desktop shows on hover -->
      <Button
        data-testid="add-service-trigger"
        variant="ghost"
        size="sm"
        class="max-sm:hidden sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
        :aria-label="t('services.actions.addService')"
        @click="store.openDraft(category.id)"
      >
        <Plus
          :size="14"
          aria-hidden="true"
        />
        {{ t("services.actions.addService") }}
      </Button>

      <!-- ⋯ overflow menu — always visible, 44px touch target on mobile -->
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button
            data-testid="overflow-menu-trigger"
            variant="ghost"
            size="icon"
            class="max-sm:size-11"
            :aria-label="t('services.a11y.categoryActions', { name: category.name })"
          >
            <MoreHorizontal
              :size="16"
              aria-hidden="true"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            data-testid="action-rename"
            @select="startRename"
          >
            {{ t("services.categories.renameCategory") }}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            class="text-destructive"
            data-testid="action-delete"
            @select="startDelete"
          >
            {{ t("services.categories.deleteCategory") }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    <!-- editing / pendingRename -->
    <div
      v-else-if="mode === 'editing' || mode === 'pendingRename'"
      class="flex items-center gap-2"
    >
      <Input
        ref="editInputRef"
        v-model="editText"
        class="flex-1 min-w-0"
        :disabled="mode === 'pendingRename'"
        :aria-label="t('services.categories.addPlaceholder')"
        @keydown.enter.prevent="commitRename"
        @keydown.esc.prevent="cancelRename"
        @blur="commitRename"
      />
      <Button
        data-testid="save-rename"
        variant="ghost"
        size="icon"
        class="max-sm:size-11"
        :disabled="!editText.trim() || mode === 'pendingRename'"
        :loading="mode === 'pendingRename'"
        :aria-label="t('services.actions.confirm')"
        @mousedown.prevent
        @click="commitRename"
      >
        <Check
          :size="20"
          aria-hidden="true"
        />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        class="max-sm:size-11"
        :disabled="mode === 'pendingRename'"
        :aria-label="t('services.actions.cancel')"
        @mousedown.prevent
        @click="cancelRename"
      >
        <X
          :size="20"
          aria-hidden="true"
        />
      </Button>
    </div>

    <!-- deleteConfirm / pendingDelete -->
    <div
      v-else
      class="flex flex-col gap-2"
    >
      <p class="text-sm font-medium">
        {{ t("services.delete.confirm", { name: category?.name ?? "" }) }}
      </p>
      <p class="text-sm text-muted-foreground">
        {{ t("services.categories.deleteKeepsServices") }}
      </p>
      <div class="flex items-center gap-2">
        <Button
          variant="destructive"
          size="sm"
          :loading="mode === 'pendingDelete'"
          @click="confirmDelete"
        >
          {{ t("services.actions.delete") }}
        </Button>
        <Button
          ref="cancelDeleteRef"
          variant="outline"
          size="sm"
          :disabled="mode === 'pendingDelete'"
          @click="cancelDelete"
        >
          {{ t("services.actions.cancel") }}
        </Button>
      </div>
    </div>
  </div>
</template>
